import React, { useEffect, useMemo, useState } from "react";
import toast from "react-hot-toast";
import { createTaxonomy, deleteTaxonomy, thumbUrl, updateTaxonomy, uploadTaxonomyImage } from "./artGalleryApi";
import { SITE_URL } from "./artGalleryShared";
import { Field, ImageField, RichText, TextField, Toggle } from "./ArtGalleryFields";

const CONFIG = {
  artists: {
    singular: "artist",
    imageField: "portrait",
    imageLabel: "Portrait or studio photo",
    path: "artists",
    blank: { name: "", slug: "", shortBio: "", bioHtml: "", featured: false, published: true, sortOrder: 100 },
  },
  subjects: {
    singular: "subject",
    imageField: "image",
    imageLabel: "Cover image",
    path: "subjects",
    blank: { name: "", slug: "", description: "", sortOrder: 50 },
  },
};

// resolves true once deleted, false if the user cancels
const confirmAndDelete = async (kind, item) => {
  const used = item.artworkCount ? ` ${item.artworkCount} artwork${item.artworkCount === 1 ? "" : "s"} will be unlinked.` : "";
  if (!window.confirm(`Delete ${item.name}?${used} This can't be undone.`)) return false;
  await deleteTaxonomy(kind, item._id);
  return true;
};

const Editor = ({ kind, item, onClose, onSaved, onDeleted, onError }) => {
  const config = CONFIG[kind];
  const isNew = !item._id;
  const [form, setForm] = useState(() => ({ ...config.blank, ...item }));
  const [busy, setBusy] = useState("");
  const set = (key) => (value) => setForm((f) => ({ ...f, [key]: value }));

  useEffect(() => {
    const onKey = (e) => e.key === "Escape" && !busy && onClose();
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [busy, onClose]);

  const fields = () => {
    const base = { name: form.name, sortOrder: Number(form.sortOrder) || 0 };
    if (form.slug && form.slug !== item.slug) base.slug = form.slug;
    if (kind === "artists") Object.assign(base, { shortBio: form.shortBio, bioHtml: form.bioHtml, featured: form.featured, published: form.published });
    else base.description = form.description;
    return base;
  };

  const save = async () => {
    if (!form.name.trim()) {
      toast.error("Add a name");
      return;
    }
    setBusy("save");
    try {
      const saved = isNew ? await createTaxonomy(kind, fields()) : await updateTaxonomy(kind, item._id, fields());
      onSaved(saved);
      toast.success("Saved");
      onClose();
    } catch (error) {
      onError(error);
      setBusy("");
    }
  };

  const remove = async () => {
    setBusy("delete");
    try {
      if (!(await confirmAndDelete(kind, item))) {
        setBusy("");
        return;
      }
      onDeleted(item._id);
      onClose();
    } catch (error) {
      onError(error);
      setBusy("");
    }
  };

  const uploadImage = async (file) => {
    const saved = await uploadTaxonomyImage(kind, item._id, file);
    onSaved(saved);
    setForm((f) => ({ ...f, [config.imageField]: saved[config.imageField] }));
    return saved[config.imageField];
  };

  return (
    <div className="ag-modal-backdrop" onMouseDown={(e) => e.target === e.currentTarget && !busy && onClose()}>
      <div className="ag-drawer" role="dialog" aria-modal="true">
        <div className="ag-drawer-head">
          <h2>{isNew ? `New ${config.singular}` : form.name}</h2>
          <button className="ag-icon-btn" onClick={onClose} aria-label="Close">
            ✕
          </button>
        </div>
        <div className="ag-drawer-body">
          <TextField label="Name" value={form.name} onChange={set("name")} autoFocus={isNew} />
          <TextField
            label="Web address"
            value={form.slug}
            onChange={set("slug")}
            placeholder="Created from the name"
            hint={form.slug ? `${SITE_URL.replace(/^https?:\/\//, "")}/${config.path}/${form.slug}` : undefined}
          />
          {isNew ? (
            <p className="ag-field-hint">Save first, then add an image.</p>
          ) : (
            <ImageField label={config.imageLabel} value={form[config.imageField]} onChange={null} upload={uploadImage} />
          )}
          {kind === "artists" ? (
            <>
              <TextField
                label="Short bio"
                multiline
                rows={3}
                value={form.shortBio}
                onChange={set("shortBio")}
                hint="A sentence or two for artist cards and the top of the artist page"
              />
              <RichText label="Full biography" value={form.bioHtml} onChange={set("bioHtml")} />
              <Toggle label="Featured" hint="Shown first on the Artists page" checked={form.featured} onChange={set("featured")} />
              <Toggle label="Published" hint="Hidden artists keep their artworks, but have no page" checked={form.published} onChange={set("published")} />
            </>
          ) : (
            <TextField label="Description" multiline rows={4} value={form.description} onChange={set("description")} />
          )}
          <Field label="Sort order" hint="Lower numbers are listed first">
            <input type="number" value={form.sortOrder} onChange={(e) => set("sortOrder")(e.target.value)} />
          </Field>
          {!isNew && item.slug && (
            <a className="ag-link-btn" href={`${SITE_URL}/${config.path}/${item.slug}`} target="_blank" rel="noreferrer">
              View on website ↗
            </a>
          )}
        </div>
        <div className="ag-drawer-foot">
          {!isNew && (
            <button className="ag-btn ag-btn-danger-ghost" onClick={remove} disabled={!!busy}>
              Delete
            </button>
          )}
          <span />
          <button className="ag-btn ag-btn-ghost" onClick={onClose} disabled={!!busy}>
            Cancel
          </button>
          <button className="ag-btn ag-btn-primary" onClick={save} disabled={!!busy}>
            {busy === "save" ? "Saving..." : "Save"}
          </button>
        </div>
      </div>
    </div>
  );
};

const TaxonomyPanel = ({ kind, items, onChange, onError }) => {
  const config = CONFIG[kind];
  const [editing, setEditing] = useState(null);
  const [search, setSearch] = useState("");

  const visible = useMemo(() => {
    const q = search.trim().toLowerCase();
    return q ? items.filter((i) => i.name.toLowerCase().includes(q)) : items;
  }, [items, search]);

  const upsert = (saved) => {
    const exists = items.some((i) => i._id === saved._id);
    const merged = exists ? items.map((i) => (i._id === saved._id ? { ...i, ...saved } : i)) : [...items, { ...saved, artworkCount: 0, publishedCount: 0 }];
    onChange(merged.sort((a, b) => a.sortOrder - b.sortOrder || a.name.localeCompare(b.name)));
  };

  const removeFromList = (id) => onChange(items.filter((i) => i._id !== id));

  const deleteRow = async (item) => {
    try {
      if (!(await confirmAndDelete(kind, item))) return;
      removeFromList(item._id);
      toast.success(`Deleted ${item.name}`);
    } catch (error) {
      onError(error);
    }
  };

  return (
    <>
      <div className="ag-toolbar">
        <input className="ag-search" type="search" placeholder={`Search ${kind}`} value={search} onChange={(e) => setSearch(e.target.value)} />
        <span className="ag-toolbar-spacer" />
        <button className="ag-btn ag-btn-primary" onClick={() => setEditing({})}>
          + Add {config.singular}
        </button>
      </div>

      {visible.length ? (
        <div className="ag-table">
          <div className="ag-table-row ag-table-head">
            <span />
            <span>Name</span>
            <span>Artworks</span>
            <span>{kind === "artists" ? "Status" : "Order"}</span>
            <span />
          </div>
          {visible.map((item) => {
            const image = item[config.imageField];
            return (
              <div
                key={item._id}
                className="ag-table-row"
                role="button"
                tabIndex={0}
                onClick={() => setEditing(item)}
                onKeyDown={(e) => {
                  if (e.target !== e.currentTarget || (e.key !== "Enter" && e.key !== " ")) return;
                  e.preventDefault();
                  setEditing(item);
                }}
              >
                <span className="ag-table-thumb">{image?.url ? <img src={thumbUrl(image.url, 240)} alt="" loading="lazy" /> : null}</span>
                <span>
                  <strong>{item.name}</strong>
                  <small>{kind === "artists" ? item.shortBio : item.description}</small>
                </span>
                <span>
                  {item.publishedCount} live
                  {item.artworkCount > item.publishedCount && <small>{item.artworkCount - item.publishedCount} draft</small>}
                </span>
                <span>
                  {kind === "artists" ? (
                    <>
                      {item.published === false ? <span className="ag-badge ag-badge-draft">Hidden</span> : <span className="ag-badge ag-badge-available">Live</span>}
                      {item.featured && <span className="ag-badge ag-badge-review">Featured</span>}
                    </>
                  ) : (
                    item.sortOrder
                  )}
                </span>
                <button
                  type="button"
                  className="ag-icon-btn ag-icon-btn-danger"
                  onClick={(e) => {
                    e.stopPropagation();
                    deleteRow(item);
                  }}
                  aria-label={`Delete ${item.name}`}
                  title={`Delete ${config.singular}`}
                >
                  ✕
                </button>
              </div>
            );
          })}
        </div>
      ) : (
        <div className="ag-empty">No {kind} yet.</div>
      )}

      {editing && (
        <Editor
          kind={kind}
          item={editing}
          onClose={() => setEditing(null)}
          onSaved={upsert}
          onDeleted={removeFromList}
          onError={onError}
        />
      )}
    </>
  );
};

export default TaxonomyPanel;
